import api from '../utils/api';

/**
 * @typedef {Object} User
 * @property {string} _id
 * @property {string} username
 * @property {string} email
 * @property {string} role - 'admin', 'instructor', 'student'
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string} [profilePicture]
 * @property {boolean} isEmailVerified
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateUserData
 * @property {string} username
 * @property {string} email
 * @property {string} password
 * @property {string} [role]
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string} [profilePicture]
 * @property {boolean} [skipEmailVerification]
 */

/**
 * @typedef {Object} UpdateUserData
 * @property {string} [username]
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string} [profilePicture]
 */

export const userService = {
  /**
   * Get All Users (Admin Only)
   * @param {Object} [params] - Optional query parameters (search, role, etc.)
   * @returns {Promise<{status: string, count: number, users: User[]}>}
   */
  getAllUsers: async (params = {}) => {
    const response = await api.get('/api/users', { params });
    return response;
  },

  // ==================
  // ADMIN EXPORT ROUTES
  // ==================

  /**
   * Get user count (Admin - for export preview)
   * @param {Object} params - Query parameters (role, isEmailVerified, fromDate, toDate, etc.)
   * @returns {Promise<{status: string, total: number}>}
   */
  getUserCount: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/users/admin/count${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response;
  },

  /**
   * Export users without pagination (Admin)
   * @param {Object} params - Query parameters (role, isEmailVerified, fromDate, toDate, etc.)
   * @returns {Promise<{status: string, count: number, users: User[]}>}
   */
  exportUsers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/users/admin/export${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response;
  },

  /**
   * Get User By ID (Admin or Owner)
   * @param {string} id 
   * @returns {Promise<{status: string, user: User}>}
   */
  getUserById: async (id) => {
    const response = await api.get(`/api/users/${id}`);
    return response;
  },

  /**
   * Create User (Admin Only)
   * @param {CreateUserData} userData 
   * @returns {Promise<{status: string, message: string, user: User}>}
   */
  createUser: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response;
  },

  /**
   * Update User (Admin or Owner)
   * @param {string} id 
   * @param {UpdateUserData} updates 
   * @returns {Promise<{status: string, message: string, user: User}>}
   */
  updateUser: async (id, updates) => {
    const response = await api.put(`/api/users/${id}`, updates);
    return response;
  },

  /**
   * Delete User (Admin or Owner)
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteUser: async (id) => {
    const response = await api.delete(`/api/users/${id}`);
    return response;
  },

  /**
   * Get My Profile
   * @returns {Promise<{status: string, user: User}>}
   */
  getMyProfile: async () => {
    const response = await api.get('/api/users/me');
    return response;
  },

  /**
   * Update My Profile
   * @param {UpdateUserData} updates 
   * @returns {Promise<{status: string, message: string, user: User}>}
   */
  updateMyProfile: async (updates) => {
    const response = await api.put('/api/users/me', updates);
    return response;
  },

  /**
   * Get devices for a specific user (Admin only)
   * @param {string} userId - ID of the user to fetch devices for
   * @returns {Promise<Object>} Response containing device list
   */
  getUserDevices: async (userId) => {
    const response = await api.get(`/api/devices/admin/all?userId=${userId}&_t=${Date.now()}`);
    return response;
  },

  /**
   * Delete a device (Admin only - Hard delete)
   * @param {string} deviceId 
   * @returns {Promise<Object>}
   */
  deleteDevice: async (deviceId) => {
    const response = await api.delete(`/api/devices/admin/${deviceId}`);
    return response;
  },

  /**
   * Block a device (Admin only)
   * @param {string} deviceId 
   * @param {string} [reason]
   * @returns {Promise<Object>}
   */
  blockDevice: async (deviceId, reason) => {
    const response = await api.put(`/api/devices/admin/${deviceId}/block`, { reason });
    return response;
  },

  /**
   * Unblock a device (Admin only)
   * @param {string} deviceId 
   * @returns {Promise<Object>}
   */
  unblockDevice: async (deviceId) => {
    const response = await api.put(`/api/devices/admin/${deviceId}/unblock`);
    return response;
  },

  /**
   * Get all device sessions (Admin only)
   * @param {Object} [params] - Query parameters
   * @param {AbortSignal} [signal] - AbortController signal for cancellation
   * @returns {Promise<{status: string, sessions: Object[]}>}
   */
  getDeviceSessions: async (params = {}, signal = null) => {
    const config = { params };
    if (signal) {
      config.signal = signal;
    }
    const response = await api.get('/api/devices/admin/all', config);
    return response;
  },

  /**
   * Revoke a device session (Admin only)
   * @param {string} sessionId
   * @returns {Promise<Object>}
   */
  revokeDeviceSession: async (sessionId) => {
    const response = await api.delete(`/api/devices/admin/${sessionId}`);
    return response;
  },

  /**
   * Reset all devices (Admin only - Hard delete ALL)
   * @returns {Promise<Object>}
   */
  resetAllDevices: async () => {
    const response = await api.delete('/api/devices/admin/reset-all');
    return response;
  },

  /**
   * Delete My Account
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteMyAccount: async () => {
    const response = await api.delete('/api/users/me');
    return response;
  },

  /**
   * Get All Available Permissions (Super Admin only)
   * @returns {Promise<{status: string, permissions: string[]}>}
   */
  getAvailablePermissions: async () => {
    const response = await api.get('/api/users/permissions/available');
    return response;
  },
};

